import { cloneDeep, isString, merge } from 'lodash';
import { AnyStateMachine } from 'xstate';
import { COMMUNITY_SOLAR_FUNNEL_ID } from '~/funnels/community-solar/config';
import { GO_SOLAR_FUNNEL_ID } from '~/funnels/go-solar/config';
import { I_SOLAR_UCB } from '~/funnels/isolar-ucb/config';
import { PartialOverride } from '~/machines/utils/types';
import { FetchFunnelConfigurationResponse } from '~/services/fetch-funnel-configuration';
import { FunnelTrack, OriginationSource } from '~/typings/graphql';
import { store } from '~/utils/store';
import { UTILITY_IDS } from './constants';

export type FunnelConfig = {
  id: string;
  path: `/${string}`;
  show: () => boolean;
};

// logic for calculating savings rate based on a user's state, utility or funnel
// Now supports getting savings rate from funnel configuration when available
export const getSavingsRateDescription = (
  stateAbbreviation: string,
  funnelId?: string,
  utilityId?: number,
  funnelConfiguration?: FetchFunnelConfigurationResponse,
): string => {
  if (funnelConfiguration?.savingsPercentage) {
    const percentage = funnelConfiguration.savingsPercentage;
    return `${percentage}%`;
  }

  const isNonABP = funnelId === 'nonABP';
  const isGoSolar = funnelId && funnelId === GO_SOLAR_FUNNEL_ID;
  const isCommunitySolar = funnelId && funnelId === COMMUNITY_SOLAR_FUNNEL_ID;
  const isIsolar = funnelId && funnelId === I_SOLAR_UCB;

  if (isNonABP) return '10-20%';
  if (stateAbbreviation === 'MD' && (isGoSolar || isCommunitySolar)) return '25%';

  // Savings percentage for Central Maine Power
  if (utilityId === UTILITY_IDS['Central Maine Power']) return '10-15%';
  if (utilityId === UTILITY_IDS['Consolidated Edison NY'] && isIsolar) return '5-20%';
  if (utilityId === UTILITY_IDS['Unitil MA']) return '10-20%';

  switch (stateAbbreviation) {
    case 'NJ': {
      return 'up to 21%';
    }
    case 'VA': {
      return '10-20%';
    }
    case 'OR': {
      return '5%';
    }
    case 'DE': {
      return '5-20%';
    }
    case 'NM': {
      return '30%';
    }
    default: {
      return '5-10%';
    }
  }
};

export const determineOriginationSource = (params: {
  funnelId?: string;
  promoCode?: string;
  utmParams?: Record<string, string | number>;
  funnelTrack?: string;
}): OriginationSource => {
  if (isUserWaitList(params.funnelTrack)) {
    return OriginationSource.Waitlist;
  }
  if (
    params.promoCode?.startsWith('isolar') ||
    params.funnelId?.includes('isolarUcb') ||
    params.funnelId?.includes('goSolar')
  ) {
    return OriginationSource.DirectSales;
  }
  if (params.funnelId?.includes('perchEnergy')) {
    return OriginationSource.PerchEnergy;
  } else if (!params.utmParams?.utm_source) {
    return OriginationSource.Organic;
  } else {
    return OriginationSource.Unknown;
  }
};

export const screenMapOverride = <TMap extends Record<any, any>>(
  screenMap: TMap,
  overrides: PartialOverride<TMap>,
): TMap => merge(cloneDeep(screenMap), overrides);

export const isUserWaitList = funnelTrack => {
  return funnelTrack === FunnelTrack.Waitlist;
};
export const getInitialState = (
  id: string,
  states: ((...args: any[]) => AnyStateMachine) | readonly string[],
): string | undefined => {
  const initialStates =
    typeof states === 'function'
      ? Object.keys(states({ id: 'getInitialStates' }).states).filter(isString)
      : states;
  const persistedState = store.getFunnel(id)?.value as string;
  if (persistedState && initialStates.includes(persistedState)) {
    return persistedState;
  } else {
    return undefined;
  }
};

export const determineLanguage = () => {
  const userLanguage = window.Localize?.getLanguage();
  // do nothing if there's no language
  if (!userLanguage) return undefined;

  const availableLanguages = () => {
    let languages: { code: string; language: string }[] = [];
    window.Localize?.getAvailableLanguages((err, _languages) => {
      // eslint-disable-next-line no-console
      if (err) return console.log(err);
      languages = _languages;
    });
    return languages;
  };

  const hasMatch = availableLanguages().some(language => {
    // strip out locale - es-LA -> es
    const [availableLanguageCode] = language.code.split('-');
    const [userLanguageCode] = userLanguage.split('-');
    return availableLanguageCode === userLanguageCode;
  });

  return hasMatch ? userLanguage.substring(0, 2) : 'en';
};
